/**
 * The ward's how-to card: a small glass card at console height on the viewer's left of the
 * filter console, tilted back like the console, so it reads on arrival without hiding any
 * patient. It says the one thing nobody guesses: holding the grip on a mannequin pulls that
 * patient out of the ward for review (grip twice sends them back; G on desktop).
 * Drawn once (one draw call); hidden while a patient is out for review.
 */
import * as THREE from 'three';
import { glassPanel, text, PALETTE, inkFor } from '../dashboards/charts.ts';

export const WARD_HINT = { width: 0.56, height: 0.2 };
const PX_W = 840;
const PX_H = 300;

export function buildWardHint(): THREE.Mesh {
  const canvas = document.createElement('canvas');
  canvas.width = PX_W;
  canvas.height = PX_H;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, PX_W, PX_H);
  glassPanel(ctx, { x: 2, y: 2, w: PX_W - 4, h: PX_H - 4 }, 28, { highlight: true });
  drawGrip(ctx, 92, PX_H / 2);
  const x = 176;
  const maxW = PX_W - x - 28;
  text(ctx, 'HOLD GRIP ON A PATIENT', x, 92, {
    size: 20,
    weight: 700,
    colour: PALETTE.accentText,
    maxWidth: maxW,
  });
  text(ctx, 'to pull them out for review', x, 156, {
    size: 30,
    weight: 600,
    display: true,
    maxWidth: maxW,
  });
  text(ctx, 'Grip twice to send them back · G on desktop', x, 222, {
    size: 17,
    colour: PALETTE.muted,
    maxWidth: maxW,
  });
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 8;
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(WARD_HINT.width, WARD_HINT.height),
    new THREE.MeshBasicMaterial({ map: texture, transparent: true, toneMapped: false }),
  );
  mesh.name = 'ward-hint';
  return mesh;
}

/** A closed hand round a handle, in a faint ring of light. */
function drawGrip(ctx: CanvasRenderingContext2D, x: number, y: number): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(1.4, 1.4);
  ctx.strokeStyle = inkFor(PALETTE.teal);
  ctx.lineWidth = 5;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.globalAlpha = 0.35;
  ctx.beginPath();
  ctx.arc(0, 0, 40, 0, Math.PI * 2);
  ctx.stroke();
  ctx.globalAlpha = 1;
  ctx.beginPath();
  ctx.moveTo(-6, -30);
  ctx.lineTo(-6, 30);
  ctx.stroke();
  for (const fy of [-18, -4, 10]) {
    ctx.beginPath();
    ctx.moveTo(-20, fy);
    ctx.quadraticCurveTo(10, fy - 8, 14, fy + 4);
    ctx.stroke();
  }
  ctx.beginPath();
  ctx.moveTo(-20, 22);
  ctx.quadraticCurveTo(-2, 28, 10, 20);
  ctx.stroke();
  ctx.restore();
}
